import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { Modal } from '@cotera/client/app/components/ui/modal/modal';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '../components/ui/sidebar';
import { Icon } from '@cotera/client/app/components/ui';
import { ErrorBoundary } from 'react-error-boundary';
import { DisplayError, Loading } from '@cotera/client/app/components';
import {
  ProfileView,
  UserManagementView,
  OrganizationView,
  PlanView,
  WarehouseView,
  IntegrationsView,
} from './settings-views';
import { ApiKeysView } from './api-keys-view';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Button } from '../components/ui/button';
import { useAuth } from '@workos-inc/authkit-react';
import { BillingSettingsView } from './billing/billing-settings';
import { LogOut, Menu, X } from 'lucide-react';
import { ReferralsSectionView } from './referrals-section';
import { useClient } from '@cotera/client/app/hooks/use-client';
import { useLogout } from '@cotera/client/app/hooks/use-logout';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLogger } from '../actions/framework/logger';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { BotsView } from './bots-view';
import { DisplaySettingsView } from '@cotera/client/app/v1/settings/display-settings.view';
import { AppModel } from '@cotera/client/app/v1/shell/app.model';
import { useStatefullyAwareInScopeModel } from '../actions/framework';

type SettingsViewId =
  | 'profile'
  | 'user-management'
  | 'organization'
  | 'plan'
  | 'billing'
  | 'warehouse'
  | 'integrations'
  | 'bots'
  | 'referrals'
  | 'display'
  | 'api-keys';

type SettingsNavItem = {
  id: SettingsViewId;
  name: string;
  icon: string;
};

type SettingsSection = {
  label: string;
  items: SettingsNavItem[];
};

const settingsSections: SettingsSection[] = [
  {
    label: 'Workspace',
    items: [
      { id: 'user-management', name: 'User Management', icon: 'users' },
      { id: 'integrations', name: 'Connections', icon: 'link' },
      { id: 'bots', name: 'Chat Bots', icon: 'message-square' },
      { id: 'plan', name: 'Rate Limits', icon: 'clock' },
      { id: 'warehouse', name: 'Warehouse', icon: 'circle-stack' },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'profile', name: 'Profile', icon: 'user' },
      { id: 'api-keys', name: 'API Keys', icon: 'key' },
      { id: 'organization', name: 'Organization', icon: 'building-office' },
      { id: 'billing', name: 'Billing', icon: 'credit-card' },
      { id: 'display', name: 'Display', icon: 'eye' },
      { id: 'referrals', name: 'Referrals', icon: 'banknotes' },
    ],
  },
];

const viewComponents: Record<SettingsViewId, React.FC> = {
  profile: ProfileView,
  'user-management': UserManagementView,
  organization: OrganizationView,
  plan: PlanView,
  billing: BillingSettingsView,
  warehouse: WarehouseView,
  integrations: IntegrationsView,
  bots: BotsView,
  referrals: ReferralsSectionView,
  display: DisplaySettingsView,
  'api-keys': ApiKeysView,
};

const getViewTitle = (viewId: SettingsViewId): string => {
  for (const section of settingsSections) {
    const item = section.items.find((i) => i.id === viewId);
    if (item !== undefined) {
      return item.name;
    }
  }
  return 'Settings';
};

export const SettingsModalContent: React.FC<{
  initialView?: SettingsViewId;
}> = ({ initialView = 'profile' }) => {
  const [activeView, setActiveView] = useState<SettingsViewId>(initialView);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const modalManager = useModalManager((s) => s.actions);
  const { role } = useAuth();
  const { logout } = useLogout();
  const isReferrerPartner = useIsReferrerPartner();
  const isAdmin = role === 'admin'; // Check if user has admin role
  const appModel = useStatefullyAwareInScopeModel(AppModel);
  const isV1 = appModel !== null;

  useTracking(CocoEvent.SettingsOpened, { initialView });

  const showItemPred = useCallback(
    (item: SettingsNavItem): boolean => {
      if (item.id === 'user-management') {
        return isAdmin;
      }

      if (item.id === 'referrals') {
        return isReferrerPartner;
      }

      if (item.id === 'display') {
        return isV1;
      }

      return true;
    },
    [isAdmin, isReferrerPartner, isV1]
  );

  // Filter out non-applicable sections
  const filteredSections = useMemo(() => {
    return settingsSections.map((section) => ({
      ...section,
      items: section.items.filter(showItemPred),
    }));
  }, [showItemPred]);

  const ActiveViewComponent = viewComponents[activeView];

  const handleItemClick = (viewId: SettingsViewId) => {
    setActiveView(viewId);
    setMobileSidebarOpen(false);
  };

  return (
    <div className="flex w-full h-full">
      <SidebarProvider className="items-start min-h-0">
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div className="md:hidden absolute inset-0 z-10 bg-background flex flex-col">
            <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
              <span className="text-sm font-medium">Settings</span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setMobileSidebarOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </header>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredSections.map((section, index) => (
                <SidebarGroup
                  key={section.label}
                  className={index === 0 ? 'py-2' : ''}
                >
                  <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {section.items.map((item) => (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton
                            className="cursor-pointer"
                            isActive={activeView === item.id}
                            onClick={() => handleItemClick(item.id)}
                          >
                            <Icon icon={item.icon as any} size="small" />
                            <span>{item.name}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
              <div className="mt-auto pt-4 border-t border-border">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className="cursor-pointer text-muted-foreground hover:text-foreground"
                      onClick={() => logout()}
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log out</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </div>
            </div>
          </div>
        )}

        {/* Desktop sidebar */}
        <Sidebar
          collapsible="none"
          className="border-r border-border hidden md:flex"
        >
          <SidebarContent>
            {filteredSections.map((section, index) => (
              <SidebarGroup
                key={section.label}
                className={index === 0 ? 'py-2' : ''}
              >
                <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {section.items.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          className="cursor-pointer"
                          isActive={activeView === item.id}
                          onClick={() => handleItemClick(item.id)}
                        >
                          <Icon icon={item.icon as any} size="small" />
                          <span>{item.name}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarFooter className="border-t border-border">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={() => logout()}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Log out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <main className="flex h-full flex-1 flex-col overflow-hidden min-w-0">
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setMobileSidebarOpen(true)}
                className="md:hidden"
              >
                <Menu className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Settings</span>
              <span className="text-sm text-muted-foreground">/</span>
              <span className="text-sm font-medium">
                {getViewTitle(activeView)}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => modalManager.close()}
            >
              <Icon icon="x-mark" />
            </Button>
          </header>
          <div className="flex-1 overflow-y-auto p-4">
            <ErrorBoundary fallbackRender={DisplayError}>
              <Suspense fallback={<Loading.Shimmer className="h-32 w-full" />}>
                <ActiveViewComponent />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </SidebarProvider>
    </div>
  );
};

export const SettingsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  initialView?: SettingsViewId;
}> = ({ open, onClose, initialView }) => {
  return (
    <Modal
      open={open}
      onClose={() => onClose()}
      closeEnabled={false}
      padding={false}
      contentClassName="p-0 overflow-hidden"
    >
      <SettingsModalContent initialView={initialView} />
    </Modal>
  );
};

// Hook to open settings modal from anywhere
export const useSettingsModal = () => {
  const modalManager = useModalManager((s) => s.actions);

  const openSettings = (initialView?: SettingsViewId) => {
    modalManager.push({
      view: () => <SettingsModalContent initialView={initialView} />,
      size: {
        custom:
          'w-[98vw] h-[85dvh] md:w-[66vw] md:h-[80vh] rounded-lg max-h-[90dvh]',
      },
    });
  };

  return { openSettings };
};

export const useIsReferrerPartner = (): boolean => {
  const client = useClient();
  const logger = useLogger();

  const { data } = useSuspenseQuery({
    queryKey: ['isReferrerPartner'],
    queryFn: async () => {
      return await client.t.referrals.getIsRefererPartner({});
    },
  });

  if (data.isErr()) {
    logger.error('Error getting isReferrerPartner (for settings modal)');
    return false;
  }

  const { isReferrerPartner } = data.value;
  return isReferrerPartner;
};
